import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, Settings, Users, Shield } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const fadeInUpStagger = (delay = 0) => ({
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] } }
});

const Terms = () => {
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);

  // Section refs
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const contentRef = useRef(null);
  const ctaRef = useRef(null);

  // InView hooks
  const headerInView = useInView(headerRef, { amount: 0.05, once: true });
  const navInView = useInView(navRef, { amount: 0.05, once: true });
  const contentInView = useInView(contentRef, { amount: 0.05, once: true });
  const ctaInView = useInView(ctaRef, { amount: 0.05, once: true });

  const scrollToSection = (sectionId: string) => {
    // SSR-safe: Only execute on client-side
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setHighlightedSection(sectionId);
      setTimeout(() => setHighlightedSection(null), 2000);
    }
  };

  return (
    <div className="h-full w-full text-foreground overflow-hidden transition-colors duration-300 landing-gradient-bg">

      {/* Animated Grid Background */}

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="pt-32 pb-16 px-8 md:px-16 xl:px-32"
      >
        <div className="max-w-screen-2xl mx-auto">
          {/* Header */}
          <motion.div ref={headerRef} variants={fadeInUp} initial="hidden" animate={headerInView ? 'visible' : 'hidden'} className="mb-12 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">Terms of Service</h1>
            <p className="text-xl text-black dark:text-gray-300">Your agreement to use Synkro's research platform</p>
          </motion.div>

          {/* Quick Navigation */}
          <motion.div ref={navRef} variants={fadeInUp} initial="hidden" animate={navInView ? 'visible' : 'hidden'} className="bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-base">
              <button onClick={() => scrollToSection('acceptance')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Acceptance of Terms</button>
              <button onClick={() => scrollToSection('service')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Service Description</button>
              <button onClick={() => scrollToSection('accounts')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">User Accounts</button>
              <button onClick={() => scrollToSection('content')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">User Content</button>
              <button onClick={() => scrollToSection('google')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Google Integration</button>
              <button onClick={() => scrollToSection('ai')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">AI Processing</button>
              <button onClick={() => scrollToSection('contact')} className="px-4 py-2 rounded-md bg-accent/20 hover:bg-blue-700 dark:hover:bg-blue-700 text-blue-800 dark:text-blue-200 hover:text-white text-left font-medium transition-colors">Contact Info</button>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div ref={contentRef} variants={fadeInUp} initial="hidden" animate={contentInView ? 'visible' : 'hidden'} className="space-y-8">
            
            <div id="acceptance" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'acceptance' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <div className="bg-blue-100 dark:bg-blue-900/20 rounded-lg p-2">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                By accessing or using Synkro ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, please do not use the Service.
              </p>
            </div>

            <div id="service" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'service' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <div className="bg-green-100 dark:bg-green-900/20 rounded-lg p-2">
                  <Settings className="h-6 w-6 text-green-600" />
                </div>
                2. Description of Service
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                Synkro is a research and project management platform that provides:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">File organization and management</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">Research project tools and AI assistance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">Google services integration</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">Note-taking and AI summaries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">Collaborative workspaces</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700 dark:text-gray-300">Data visualization and charting</span>
                  </div>
                </div>
              </div>
            </div>

            <div id="accounts" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'accounts' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-purple-100 dark:bg-purple-900/20 rounded-lg p-2">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                3. User Accounts and Registration
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">3.1 Account Creation</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You must provide accurate and complete information when creating an account</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You are responsible for maintaining the confidentiality of your account credentials</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You are responsible for all activities that occur under your account</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You must notify us immediately of any unauthorized use of your account</span>
                    </div>
                  </div>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">3.2 Eligibility</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You must be at least 13 years old to use the Service</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">If you are under 18, you represent that you have parental consent to use the Service</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div id="content" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'content' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-orange-100 dark:bg-orange-900/20 rounded-lg p-2">
                  <Shield className="h-6 w-6 text-orange-600" />
                </div>
                4. User Content and Data
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">4.1 Your Content</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You retain ownership of all content you upload, create, or store in Synkro</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">By using the Service, you grant us a limited license to store, process, and display your content solely for the purpose of providing the Service</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You are solely responsible for the content you upload and must ensure you have the right to use and share it</span>
                    </div>
                  </div>
                </div>

                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">4.2 Prohibited Content</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">You may not upload, store, or share content that:</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-300">Violates any applicable laws or regulations</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-300">Infringes on intellectual property rights of others</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-300">Contains malware, viruses, or other harmful code</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-300">Is defamatory, harassing, or abusive</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
                      <span className="text-gray-600 dark:text-gray-300">Contains personally identifiable information of others without consent</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div id="google" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'google' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-red-100 dark:bg-red-900/20 rounded-lg p-2">
                  <Settings className="h-6 w-6 text-red-600" />
                </div>
                5. Google Services Integration
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.1 Third-Party Services</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">Synkro integrates with Google services (Calendar, Drive, Gmail, Meet, People API)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">Your use of Google services through Synkro is subject to Google's Terms of Service and Privacy Policy</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">We are not responsible for the availability, functionality, or policies of third-party services</span>
                    </div>
                  </div>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.2 Data Access</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You grant Synkro permission to access your Google services data as necessary to provide the integrated features</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You can revoke these permissions at any time through your Google account settings</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div id="ai" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'ai' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-indigo-100 dark:bg-indigo-900/20 rounded-lg p-2">
                  <Users className="h-6 w-6 text-indigo-600" />
                </div>
                6. AI and Automated Processing
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">6.1 AI Features</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">Synkro uses artificial intelligence to provide summaries, suggestions, and automated assistance</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">AI-generated content is provided "as is" and may not always be accurate</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">You should review and verify all AI-generated content before relying on it</span>
                    </div>
                  </div>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">6.2 Data Processing</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-indigo-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">We may process your content to provide AI features and improve the Service</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-indigo-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-600 dark:text-gray-300">All processing is done in accordance with our Privacy Policy</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div id="contact" className={`bg-white/80 dark:bg-[#18181b]/80 border border-gray-200 dark:border-white/[0.05] backdrop-blur rounded-xl shadow-lg p-8 transition-all duration-500 ${highlightedSection === 'contact' ? 'ring-2 ring-blue-500 shadow-blue-500/20' : ''}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="bg-gray-100 dark:bg-gray-900/20 rounded-lg p-2">
                  <Settings className="h-6 w-6 text-gray-600" />
                </div>
                7. Contact Information
              </h2>
              
              <div className="text-center space-y-4">
                <p className="text-gray-600 dark:text-gray-300 mb-6">If you have questions about these Terms, please contact us:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Email Support</h4>
                    <p className="text-blue-600 dark:text-blue-400">info@synkroai.app</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Business Address</h4>
                    <p className="text-purple-600 dark:text-purple-400">
                      Building 32<br />
                      Fidar, Byblos<br />
                      Lebanon
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div ref={ctaRef} variants={fadeInUp} initial="hidden" animate={ctaInView ? 'visible' : 'hidden'} className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl text-white text-center">
              <p className="text-blue-100 mb-4">
                <strong>Ready to get started?</strong> By signing up, you agree to these Terms of Service.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/privacy">
                  <Button variant="secondary" size="lg">
                    View Privacy Policy
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="secondary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                    Accept & Sign Up
                  </Button>
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll to Top Button */}
    </div>
  );
};

export default Terms; 